/**
 * A company's PROJECTS OF INTEREST, shaped as seeded draft line items.
 *
 * Owner's instruction, 2026-09-13: pressing the "+" on a COMPANY row should open
 * the quote / invoice / deal create form with its line items already filled in
 * from that company's projects of interest — one line per project, carrying the
 * project's product, and where a project sells several products one line each,
 * the participation fee first and the travel package after it.
 *
 * ⚠️ **WHICH products, since `seedsLineItems`: only those an administrator has
 * set to pull in.** The flag is per `(project, product)` and lives on
 * {@link ProjectProductRef}; it was introduced defaulted to ON, so switching it
 * on changed nothing for anybody. **It decides membership and nothing else** —
 * the participation-first / travel-last reading order is a separate decision in
 * a separate function, and the two must stay that way. Three entry points:
 * {@link productSeedsLineItems} (does this one pull in — and the ONE place an
 * absent flag is decided), {@link seededLinesForProjects} (applies it), and
 * {@link projectSeedingState} (what the chooser tells a reader about a project
 * that will contribute nothing).
 *
 * ⛔ **NOTHING HERE FETCHES OR WRITES. IT DECIDES.** The same standing rule as
 * `@/lib/company-projects`, which this sits beside, and for the same reason: the
 * caller owns its own requests and its own draft state, and this module answers
 * only *"given these projects and this catalogue, what lines should the form
 * open with?"*. **There are no hooks here and none may be added** — a hook drags
 * a fetch in behind it, and the callers need different fetches (a list row has a
 * company's stored projects in hand already; a form needs the catalogue too).
 *
 * ⚠️ **SEEDING IS NOT VALIDATING**, inherited verbatim from
 * `@/components/sales-tracker/combine-pursuits`. The seven line-project refusals
 * in `@/lib/line-project` are server-side and stay there — a project that does
 * not sell a line's product is refusal 5, a retired project refusal 4 — because
 * they run on the POST-MERGE state only the server can see. Nothing here
 * reproduces them, and a seeded line meeting one is a `422` that routes to that
 * row's own control, which is the designed outcome rather than a gap.
 *
 * ## Why this is a second module and not more of `company-projects.ts`
 *
 * That module is about what a **cell** says and what a **picker** offers: seven
 * render sites, all of them reading or editing the set itself. This is about
 * turning that set into **lines on a document a customer receives**, which needs
 * the product catalogue and knows about `PlannedProductLine`. Folding the two
 * together would make every one of those seven dense cells import a line-item
 * type to render a name.
 */

// ⚠️ **`plannedLineOf` IS IMPORTED RATHER THAN MIRRORED, and the direction of
// this import is deliberate.** It lives under `components/` because the combine
// flow was its first caller, but it is a pure function with no React in it, and
// it is **the one place a seeded draft row is built** — its rules (a `null`
// description leaves the cell DERIVING, a `null` price omits the key so the
// server snapshots the catalogue figure, and NO CITIES EVER, because a city
// write is a whole-set replace a seed cannot honestly guess at) are decisions
// this feature wants unchanged. A second builder here would be a second home for
// every one of them, and the two would agree the day they were written and
// diverge on the first correction — on a document a customer is billed against.
// `@/lib/company-projects` already imports a type from `@/components/ui`, so the
// direction is not novel; if it ever wants to be tidied, `plannedLineOf` moves
// DOWN into `lib/`, and both callers follow it.
import { plannedLineOf } from '@/components/sales-tracker/combine-pursuits';
import type { PlannedProductLine } from '@/lib/api';
import type { Product } from '@/lib/types';

/**
 * Every product that some OTHER product names as its travel product.
 *
 * ⛔ **THE TRAVEL PACKAGE IS IDENTIFIED BY A REAL FOREIGN KEY AND NEVER BY A
 * NAME.** `Product.travelProduct` is an association — `f_products.travel_product_id`
 * — so "is this a travel package?" is answerable as *"does anything point at
 * it?"* and needs no string anywhere. **Nothing here may match `productName`,
 * not with `includes`, not with a regex, not case-folded against a list.** This
 * codebase has been bitten three times by matching admin-editable free text, and
 * the worst of them is this exact shape: a `/master ?class/i` test over a product
 * name switched campaign inclusions off across the catalogue on an ordinary
 * rename, with a `200` and nothing failing. A migration seeded `isAddOn` once, by
 * name, precisely so that no runtime rule ever has to.
 *
 * ⚠️ **`isAddOn` IS A DIFFERENT FLAG AND IS NOT A SUBSTITUTE FOR THIS.** It
 * marks add-on products generally, and travel products are **deliberately not
 * given it** — `Product.isAddOn`'s own doc says so in terms. The two mechanisms
 * exist because only one of the two facts was ever recorded in the schema: the
 * add-ons had nothing but their name, so they got a column; travel packages
 * already had a foreign key, so they did not need one. **Reading `isAddOn` here
 * would silently order travel lines as ordinary products**, which is the
 * backwards reading {@link orderProductsForSeeding} exists to prevent.
 *
 * ⚠️ **A SHORT `products` LIST UNDER-REPORTS, and it fails in the safe
 * direction.** `/products` pages, so a caller that read one page hands over a
 * partial catalogue and a travel product whose only referrer sits on page two is
 * not recognised. The cost is one line ordered as though it were a participation
 * fee — a reading order somebody can see and fix — where the opposite failure
 * (claiming travel on a product nothing points at) would push a real
 * participation line below a package it does not belong to. Nothing here may
 * page or fetch to close that; pass the whole catalogue.
 *
 * ⚠️ **A product may be the travel package for several products**, and the set
 * collapses that correctly — it answers *is it one*, never *whose*.
 */
export function travelProductIds(products: readonly Product[]): Set<number> {
  const ids = new Set<number>();
  for (const product of products) {
    if (product.travelProduct !== null) ids.add(product.travelProduct.id);
  }
  return ids;
}

/**
 * One product a project sells, as this module needs it: a name to print, an id
 * to key a line on, and **whether an administrator has set it to pull into a
 * new document**.
 *
 * ── 🔴 WHY `seedsLineItems` IS OPTIONAL HERE AND REQUIRED ON THE WIRE ──────
 *
 * {@link ProjectProductRef} in `@/lib/types` declares it **required**, because
 * that is what the API sends: every `f_project_products` row has the column and
 * existing rows were defaulted to `true`. **That type is the contract and this
 * one is not** — `SeedableProject` is structural on purpose (see its doc), and
 * an absent flag is reachable here by a route that has nothing to do with a
 * caller being sloppy:
 *
 *  - ⚠️ **The bare `{id, name}` rows a COMPANY read carries.** `SeedableProject`
 *    is satisfied by three different shapes (see its doc), and a company's
 *    projects of interest arrive with no flag at all. **This is the route that
 *    keeps the field optional** now that the deprecated-singular one has gone.
 *    ⚠️ *SUPERSEDED 2026-09-18: "⛔ **`Project.product`, the DEPRECATED
 *    SINGULAR, is a `MasterRef` and can never carry the flag.** Two files still
 *    hold a `products.length > 0 ? products : [product]` fallback for a server
 *    older than migration 156 … That is not a hypothetical older server; it is
 *    code in this repo today."* **It is not code in this repo any more** — there
 *    were three of those fallbacks, not two, and all three were removed so the
 *    backend could withdraw the singular. Quoted rather than deleted because the
 *    REASON for the optionality moved rather than expired, and a reader meeting
 *    a bare optional flag with no history cannot tell it from an oversight.
 *  - this app deploys separately from the API and can be **newer** than it, so
 *    a response predating the flag is an ordinary answer rather than a fault —
 *    the same reason `meta.bandTotals` is optional and read with `?.`.
 *
 * ── ⛔ SO AN ABSENT FLAG IS RESOLVED AS **SEEDING**, AND HERE IS WHY ───────
 *
 * **Resolved towards ON.** Three independent reasons, and the third is the one
 * that decides it:
 *
 *  1. it is the backend's own default for every existing row, so "absent" and
 *     "what the column would have said" agree by construction;
 *  2. read as OFF, a project reached through the singular fallback contributes
 *     **no lines at all**, so the company "+" opens an EMPTY basket — and the
 *     chooser would then disable the row saying *no product is set to pull in*,
 *     which is a **confident false statement**: nothing was set either way,
 *     the flag was simply never read;
 *  3. **fail towards the visible wrong answer.** Seeding a line nobody wanted
 *     puts a pre-ticked row in a chooser the person is looking at, which they
 *     un-tick before anything is saved. NOT seeding a line somebody did want
 *     produces a document short of work a customer is being billed for, with a
 *     `201` and nothing on any screen saying anything was withheld.
 *
 * ⛔ **THIS IS WRITTEN AS `!== false`, IN ONE PLACE, AND NEVER AS `?? true`.**
 * The two behave identically on `boolean | undefined` and are not the same
 * thing: a coalesce manufactures a VALUE, which then flows on and gets stored,
 * compared and re-defaulted at every site it reaches, and it needs the type
 * widened to swallow it. {@link productSeedsLineItems} produces a **verdict**
 * at one named site, leaves the field's own type honest about the third state,
 * and is the only thing anywhere that gets to decide this.
 *
 * ⛔ **AND `!== false` IS NOT REDUNDANT — DO NOT SIMPLIFY IT TO `!x`.** It
 * reads as a tautology the moment somebody reasons from
 * {@link ProjectProductRef} alone, where the field really is a required
 * boolean; collapsing it turns "not read yet" back into "refused", silently,
 * and every instrument in this repo stays green.
 */
export interface SeedableProjectProduct {
  id: number;
  name: string;
  /** Optional HERE and required on the wire — see this type's own doc. */
  seedsLineItems?: boolean;
}

/**
 * **Does this product seed a line?** The one place an absent flag is decided;
 * see {@link SeedableProjectProduct} for which way and why.
 *
 * ⚠️ **It answers about SEEDING only.** A `false` does not mean the project has
 * stopped selling the product, does not stop a person adding the line by hand
 * in the grid, and does not change the reading order a seeded basket comes out
 * in — {@link orderProductsForSeeding} owns that and owns nothing else.
 */
export function productSeedsLineItems(product: SeedableProjectProduct): boolean {
  return product.seedsLineItems !== false;
}

/**
 * Why a project contributes no lines — **three states, because two of them are
 * different facts and a boolean would conflate them.**
 *
 *  - `seeds` — at least one product will pull in. The row is choosable.
 *  - `none-set-to-seed` — the project sells products and **an administrator has
 *    unticked every one of them.** A real, deliberate configuration. Owner's
 *    decision: **show the row, disabled, and say this.**
 *  - `no-products-read` — the project carries no products at all, which per
 *    {@link SeedableProject.products} is a FAILED OR SHORT READ and never a
 *    project that sells nothing (`d_projects.product_id` is `NOT NULL`).
 *
 * ⛔ **The last two must not share a sentence.** One is *somebody decided this*
 * and the other is *we could not find out* — opposite instructions to the
 * reader, and telling them a configuration exists when the read simply failed
 * sends them to edit a project that is already correct. Same `0`-versus-`null`
 * distinction this codebase refuses to collapse everywhere else.
 */
export type ProjectSeedingState = 'seeds' | 'none-set-to-seed' | 'no-products-read';

/**
 * {@link ProjectSeedingState} for one project — the chooser dialog's predicate,
 * so it never re-derives this and the two cannot drift.
 *
 * ⚠️ **Safe to call on a row whose `products` has already been through
 * {@link orderProductsForSeeding}**, which orders and de-duplicates and
 * deliberately filters nothing, so the non-seeding members are still there to
 * be counted.
 */
export function projectSeedingState(project: SeedableProject): ProjectSeedingState {
  if (project.products.length === 0) return 'no-products-read';
  return project.products.some(productSeedsLineItems) ? 'seeds' : 'none-set-to-seed';
}

/**
 * One project of interest, with the products it sells, ready to become lines.
 *
 * ⚠️ **Structural on purpose, and deliberately NOT a wire type**, the same
 * choice `CompanyProject` in `@/lib/company-projects` makes and for the same
 * reason. Three different shapes satisfy it — a `Project` off `/projects`, the
 * `{id, name}` rows a company read carries once its products have been looked
 * up, and whatever a caller assembles — and naming a response type here would
 * make this module wrong the moment that response moved.
 *
 * ⚠️ **`products` IS THE SET (`f_project_products`), NOT THE DEPRECATED PRIMARY.**
 * Since migration 156 a project sells several and `products[0]` is the primary by
 * the read's own `ORDER BY`. A caller resolving this from a `Project` must fall
 * back to the singular `product` **only** for a server predating that
 * expand/contract — `productsOf` in `combine-pursuits.ts` is the expression to
 * copy. Reading the primary alone silently drops every secondary product, which
 * here means silently dropping a travel package from the document.
 */
export interface SeedableProject {
  id: number;
  name: string;
  /**
   * ⚠️ **`[]` MEANS THE PROJECT COULD NOT BE READ, NOT THAT IT SELLS NOTHING.**
   * `d_projects.product_id` stayed `NOT NULL` as the primary and the server
   * re-asserts it into the set on every write, so a project **always** sells at
   * least one product. See {@link seededLinesForProjects} for what is done
   * about it, and {@link projectSeedingState}, which keeps this apart from the
   * project whose products are all deliberately unticked.
   *
   * ⚠️ **The entries carry `seedsLineItems` and a bare `{id, name}` still
   * satisfies them** — see {@link SeedableProjectProduct} for why the flag is
   * optional on this structural type while being required on the wire.
   */
  products: readonly SeedableProjectProduct[];
}

/** What {@link seededLinesForProjects} needs. Plain data, so a test calls it directly. */
export interface SeededProjectLinesInput {
  /**
   * The company's projects of interest, in the order they should read.
   *
   * ⚠️ **The order is the CALLER'S and is preserved exactly.** Nothing here
   * re-sorts projects: the company read returns them in a settled order and the
   * line grid renders rows in the order it is handed, so re-ordering here would
   * make the form disagree with the cell somebody pressed "+" on.
   */
  projects: readonly SeedableProject[];
  /** From {@link travelProductIds}, over the catalogue. */
  travelProductIds: ReadonlySet<number>;
}

/**
 * The company's projects of interest, as draft lines for `prefill.lines`.
 *
 * **One line per product per project** — owner's decision. A project selling one
 * product gives one line; a project selling a participation fee plus a travel
 * package gives two; a project selling three or more gives three or more.
 * **Nothing is silently dropped**, and in particular nothing here decides that a
 * secondary product is "not the real one" — that decision does not exist in the
 * data and inventing it would leave a customer's document short of work the
 * project actually sells.
 *
 * 🔴 **THE LINE'S PROJECT IS WHY THE FEATURE WORKS AT ALL.** `plannedLineOf`
 * carries `{id, name}` and both halves are load-bearing: `f_quote_products.project_id`
 * is what a document's lines say the work is FOR, and the server's sales-pursuit
 * sync advances a card when a document names its `(company, project)` pair — so
 * a document whose lines carry no project advances **nothing**, with the document
 * itself perfectly correct, a `201`, and nothing failing anywhere. The name
 * travels beside the id because the line grid's project `Combobox` falls back to
 * its placeholder for a value absent from its options, which is every first
 * paint: drop it and the screen says *no project* over a wire that says one.
 *
 * ⚠️ **THE SAME PRODUCT UNDER TWO DIFFERENT PROJECTS IS LEGAL AND MUST NOT BE
 * DE-DUPLICATED.** Line identity is `(parent_id, product_id, project_key)` on all
 * three line tables, so two projects both selling the participation fee are two
 * lines and that is the whole reason `project_key` is in the key. Collapsing
 * them would be the "exclude products already added" mistake this codebase
 * already records — it hides a line the document is meant to allow.
 *
 * ⚠️ **A PROJECT WITH NO PRODUCTS CONTRIBUTES NO LINES, SILENTLY, AND THAT IS
 * THE HONEST ANSWER RATHER THAN THE TIDY ONE.** An empty `products` is a failed
 * or short read (see {@link SeedableProject.products}), never a project that
 * sells nothing — but there is nothing here to seed from: a line needs a
 * `productId`, and the only alternatives are fabricating one or refusing to seed
 * the whole form. Both are worse. **The caller is what owes the reader a word
 * about it**, and it is the only layer that can tell a short read from a failed
 * one, because it made the request.
 *
 * ⛔ **A PROJECT WHOSE PRODUCTS ARE ALL UNTICKED ALSO CONTRIBUTES NO LINES, AND
 * IT IS A DIFFERENT FACT FROM THE ONE ABOVE.** That one is *we could not find
 * out*; this one is *an administrator decided*. Owner's decision: the chooser
 * **shows that row, disabled, with the reason** rather than dropping it, because
 * a project silently missing from the dialog reads as the company's projects of
 * interest being wrong. {@link projectSeedingState} is the predicate that tells
 * the two apart — call it; do not re-derive it from `products.length` and a
 * `.some()`, which is how the two facts get collapsed back into one.
 */
export function seededLinesForProjects(input: SeededProjectLinesInput): PlannedProductLine[] {
  const { projects, travelProductIds: travelIds } = input;

  const lines: PlannedProductLine[] = [];
  for (const project of projects) {
    /*
     * ⛔ **FILTERING AND ORDERING ARE TWO JOBS AND ARE TWO FUNCTIONS.**
     * `productSeedsLineItems` decides WHETHER a product becomes a line;
     * `orderProductsForSeeding` decides only WHERE it reads. The travel
     * classification must never be made to do the first — a travel package is
     * real work a customer is being billed for, and its own doc says so.
     *
     * ⚠️ **Filtered BEFORE ordering, not after, and the order matters for one
     * reason**: the ordering pass de-duplicates by id, so a non-seeding entry
     * arriving ahead of a seeding one with the same id would win the dedupe and
     * then be dropped, taking the line with it. `f_project_products` is unique
     * on `(project_id, product_id)` so that pair should never arrive — which is
     * exactly the kind of "should never" this file already declines to rely on
     * one line further down.
     */
    const seeding = project.products.filter(productSeedsLineItems);
    for (const product of orderProductsForSeeding(seeding, travelIds)) {
      lines.push(
        plannedLineOf({
          /*
           * ⚠️ **QUANTITY IS 1 ON EVERY SEEDED LINE — owner's decision, and
           * `null` is how it is said rather than a gap in this call.** Nothing in
           * a project, and nothing in a company's projects of interest, records
           * HOW MANY of anything: `f_company_projects` carries the FACT of
           * interest and no figure. `plannedLineOf` already seeds `1` for a
           * pursuit that records no units, and `1` is what `blankDraft()` opens
           * every hand-added line at, so the row arrives exactly as it would if
           * somebody had added it themselves. Passing a literal `1` here would
           * be this module inventing a quantity; passing `null` says there was
           * never one to preserve, and keeps the two callers agreeing.
           */
          units: null,
          projectId: project.id,
          projectName: project.name,
          product,
        }),
      );
    }
  }
  return lines;
}

/**
 * A project's products, ordered **participation-first, travel last**.
 *
 * ⚠️ **ORDERING IS THE ONLY THING THE TRAVEL CLASSIFICATION DECIDES HERE.** It
 * does not exclude a line, does not change a price, does not touch a quantity
 * and must never be made to: a travel package is real work a customer is being
 * billed for, and the tracker's own exclusion of these lines
 * (`Product.isAddOn`'s doc) is about *rows on a tracker*, not about *lines on a
 * document*. A travel line printed ABOVE the participation line it belongs to
 * simply reads backwards — the reader meets the extra before the thing it is
 * extra to — and that is the whole defect this function fixes.
 *
 * ⛔ **AND IT STILL DOES NOT FILTER, NOW THAT SOMETHING ELSE DOES.**
 * `seedsLineItems` is what decides whether a product becomes a line, and
 * {@link productSeedsLineItems} is where that is answered;
 * {@link seededLinesForProjects} applies it BEFORE calling this. **Two jobs,
 * two functions** — folding the flag in here would give one function a reason
 * to drop a row, and the next person wanting to drop a row for a different
 * reason would put it here too, until "order" meant "decide". This function
 * returns everything it was handed, re-ordered, always.
 *
 * ⚠️ **A STABLE PARTITION, NOT A SORT.** Within each group the project's own
 * order survives untouched, because `products` arrives primary-first from the
 * read's `ORDER BY` and that order is the administrator's. A comparator would
 * also be tempting to extend with a name tiebreak, which is a name match wearing
 * a sort's clothes.
 *
 * ⚠️ **A REPEATED PRODUCT ID WITHIN ONE PROJECT IS COLLAPSED.**
 * `f_project_products` is unique on `(project_id, product_id)` so it should not
 * arrive — but two lines with one `(product, project)` pair are a `409` on the
 * line identity that takes the **whole save** with it, long after the person has
 * finished filling the form in, and it would read as the create being broken.
 * `projectOfInterestOptions` guards its chips the same way for the same reason.
 * ⛔ **This is WITHIN one project only** — see {@link seededLinesForProjects} on
 * why the same product under two projects must survive.
 */
export function orderProductsForSeeding(
  products: readonly SeedableProjectProduct[],
  travelProductIds: ReadonlySet<number>,
): SeedableProjectProduct[] {
  const participation: SeedableProjectProduct[] = [];
  const travel: SeedableProjectProduct[] = [];
  const seen = new Set<number>();

  for (const product of products) {
    if (seen.has(product.id)) continue;
    seen.add(product.id);
    if (travelProductIds.has(product.id)) travel.push(product);
    else participation.push(product);
  }

  return [...participation, ...travel];
}
